import React, { useCallback, useState } from "react";
import { DA_UserInformation } from "@danishagro/shared/src/interfaces/userInformation.interface";
import { isPhoneNumberValid } from "@danishagro/shared/src/helpers/validatePhoneNumber.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useUserInformationApi } from "@danishagro/shared/src/hooks/api/useUserInformationApi.hook";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";

export const useOnSubmit = (params: {
    currentData: DA_UserInformation;
    formRef: React.MutableRefObject<HTMLFormElement>;
    onSuccess: (data: DA_UserInformation) => void;
    setErrors: (errors: string[]) => void;
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { updateName } = useAppData();
    const { closeOverlay } = useDynamicOverlay();
    const { setUserInfo } = useUserInformationApi();

    const { currentData, formRef, onSuccess, setErrors } = params;

    const onSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (formRef.current) {
                const formValues: DA_UserInformation = {
                    firstName: "",
                    lastName: "",
                    email: currentData.email,
                    phone: "",
                    cellPhone: "",
                };

                // Check if all required inputs have a value,
                // and if phone numbers are valid
                const inputsWithError: string[] = [];
                Array.from(formRef.current.elements).forEach((element: HTMLInputElement) => {
                    if (element.tagName === "INPUT") {
                        if (
                            (element.required && !element.value) ||
                            (element.value &&
                                element.type === "tel" &&
                                !isPhoneNumberValid(element.value))
                        ) {
                            inputsWithError.push(element.name);
                        } else {
                            formValues[element.name] = element.value;
                        }
                    }
                });
                setErrors(inputsWithError);

                // If no errors - send data to API
                if (!inputsWithError.length) {
                    setIsSubmitting(true);
                    setUserInfo(formValues)
                        .then((response) => {
                            updateName(response);
                            onSuccess(response);
                            closeOverlay();
                        })
                        .finally(() => setIsSubmitting(false));
                }
            }
        },
        [closeOverlay, currentData.email, formRef, onSuccess, setErrors, updateName, setUserInfo]
    );

    return { onSubmit, isSubmitting };
};
